import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Download, FileText, Folder, Search, Eye, X, Calendar, Lock, FileSignature, ScrollText } from "lucide-react";
import { api } from "../lib/api";

const STATUSES = ["bozza", "inviato", "in_valutazione", "valorizzato", "chiuso", "abbandonato"];
const STATUS_COLORS = {
  bozza: "bg-slate-200 text-slate-700",
  inviato: "bg-blue-100 text-blue-800",
  in_valutazione: "bg-amber-100 text-amber-800",
  valorizzato: "bg-green-100 text-green-800",
  chiuso: "bg-slate-300 text-slate-700",
  abbandonato: "bg-rose-100 text-rose-800",
};

const TRADER_CATEGORIES = [
  { key: "proforma", label: "Proforma", icon: FileSignature },
  { key: "conferma_ordine", label: "Conferma ordine", icon: ScrollText },
];

export default function AdminProjects({ accountType = "studio" }) {
  const isTrader = accountType === "trader";
  const [view, setView] = useState("active"); // active | archived
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [files, setFiles] = useState([]);
  const [valueInput, setValueInput] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const reload = useCallback(async () => {
    const url = view === "archived"
      ? `/admin/projects/archived?account_type=${accountType}`
      : `/admin/projects/by-studio?account_type=${accountType}`;
    const { data } = await api.get(url);
    setGroups(data);
    if (selected) {
      const fresh = data.flatMap(g => g.projects).find(p => p.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  }, [selected, view, accountType]);

  useEffect(() => {
    // Reset groups quando cambia view per evitare di mostrare dati di view diversa con shape diversa.
    setGroups([]);
    reload().catch(() => toast.error("Errore caricamento"));
  }, [view, accountType]);

  async function openProject(p) {
    setSelected(p);
    setValueInput(p.value > 0 ? String(p.value) : "");
    const { data } = await api.get(`/projects/${p.id}/files`);
    setFiles(data);
  }

  async function uploadFile(pid, file, category = "generic") {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/projects/${pid}/files`, fd);
      toast.success("File caricato");
      const { data } = await api.get(`/projects/${pid}/files`);
      setFiles(data);
      reload();
    } catch { toast.error("Errore upload"); }
  }

  async function saveDeliveryDate(pid, dateStr) {
    try {
      await api.put(`/admin/projects/${pid}/delivery-date`, { delivery_date: dateStr || null });
      toast.success(dateStr ? "Data consegna salvata" : "Data consegna rimossa");
      reload();
    } catch { toast.error("Errore salvataggio data"); }
  }

  async function setStatus(pid, status) {
    try {
      await api.put(`/admin/projects/${pid}/status`, { status });
      toast.success("Stato aggiornato");
      reload();
    } catch { toast.error("Errore stato"); }
  }

  async function restoreProject(pid) {
    try {
      await api.put(`/admin/projects/${pid}/restore`);
      toast.success("Progetto ripristinato");
      setSelected(null);
      reload();
    } catch { toast.error("Errore ripristino"); }
  }

  async function setValue(pid) {
    const v = parseFloat(valueInput);
    if (isNaN(v) || v < 0) { toast.error("Valore non valido"); return; }
    try {
      const { data } = await api.put(`/admin/projects/${pid}/value`, { value: v });
      if (isTrader) {
        toast.success(`Valore impostato: € ${v.toLocaleString("it-IT")}`);
      } else {
        toast.success(`Valore impostato: € ${v.toLocaleString("it-IT")} · credito +€${(data.credit_earned || 0).toFixed(2)}`);
      }
      reload();
    } catch { toast.error("Errore valorizzazione"); }
  }

  async function downloadFile(fid, filename) {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download fallito"); }
  }

  const [filePreview, setFilePreview] = useState(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);

  function isFilePreviewable(f) {
    const ct = (f.content_type || "").toLowerCase();
    const fn = (f.filename || "").toLowerCase();
    if (ct === "application/pdf" || fn.endsWith(".pdf")) return true;
    if (ct.startsWith("image/")) return true;
    return false;
  }

  async function previewFile(f) {
    if (!isFilePreviewable(f)) return;
    const ct = (f.content_type || "").toLowerCase();
    const isPdf = ct === "application/pdf" || (f.filename || "").toLowerCase().endsWith(".pdf");
    let pdfTab = null;
    if (isPdf) {
      pdfTab = window.open("about:blank", "_blank");
      if (!pdfTab) {
        toast.error("Il browser ha bloccato l'apertura. Consenti i pop-up.");
        return;
      }
    }
    setFilePreviewLoading(true);
    try {
      const r = await api.get(`/files/${f.id}/preview`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: f.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (isPdf && pdfTab) {
        pdfTab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        setFilePreview({ url, name: f.filename, isPdf: false, isImage: ct.startsWith("image/") });
      }
    } catch {
      if (pdfTab) {
        try { pdfTab.close(); } catch (_) { /* ignore */ }
      }
      toast.error("Anteprima non disponibile");
    }
    finally { setFilePreviewLoading(false); }
  }

  function closeFilePreview() {
    if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
    setFilePreview(null);
  }

  const filteredGroups = useMemo(() => {
    const k = query.trim().toLowerCase();
    return groups
      .map((g) => {
        const matchesGroup = !k
          || (g.studio || "").toLowerCase().includes(k)
          || (g.user_name || "").toLowerCase().includes(k)
          || (g.user_email || "").toLowerCase().includes(k);
        const projects = (g.projects || []).filter((p) => {
          if (statusFilter !== "all" && p.status !== statusFilter) return false;
          if (!k) return true;
          if (matchesGroup) return true;
          return (
            (p.name || "").toLowerCase().includes(k) ||
            (p.client_name || "").toLowerCase().includes(k) ||
            (p.description || "").toLowerCase().includes(k)
          );
        });
        return { ...g, projects };
      })
      .filter((g) => g.projects.length > 0);
  }, [groups, query, statusFilter]);

  return (
    <div className="space-y-6" data-testid="admin-projects">
      <div className="flex items-center gap-1" data-testid="admin-projects-view-toggle">
        <button
          type="button"
          onClick={() => setView("active")}
          className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase rounded-sm transition-colors ${view === "active" ? "bg-ink text-cream" : "bg-cream2 text-slate1 hover:text-ink"}`}
          data-testid="adm-proj-view-active"
        >
          Attivi
        </button>
        <button
          type="button"
          onClick={() => setView("archived")}
          className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase rounded-sm transition-colors ${view === "archived" ? "bg-rose-700 text-cream" : "bg-cream2 text-slate1 hover:text-ink"}`}
          data-testid="adm-proj-view-archived"
        >
          Abbandonati
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3" data-testid="admin-projects-search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isTrader ? "Cerca per trader, progetto, cliente, email..." : "Cerca per studio, progetto, cliente, email..."}
            className="w-full pl-10 pr-4 py-3 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
            data-testid="admin-projects-search-input"
          />
        </div>
        {view === "active" && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-ink/20 px-3 py-3 rounded-sm bg-white text-sm focus:outline-none focus:border-ink md:w-56"
            data-testid="admin-projects-status-filter"
          >
            <option value="all">Tutti gli stati</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")}</option>
            ))}
          </select>
        )}
      </div>

      {filteredGroups.length === 0 && (
        <div className="text-slate1 text-sm">
          {groups.length === 0
            ? (view === "archived" ? "Nessun progetto abbandonato." : "Nessun progetto caricato dagli studi.")
            : "Nessun risultato per la ricerca corrente."}
        </div>
      )}
      {filteredGroups.map((g) => (
        <div key={g.studio} className="bg-white border border-ink/10 rounded-sm" data-testid={`studio-${g.studio}`}>
          <div className="p-5 border-b border-ink/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{isTrader ? "Trader" : "Studio"}</div>
              <div className="font-display text-lg text-ink">{g.studio || "—"}</div>
              <div className="text-xs text-slate1">{g.user_name} · {g.user_email}</div>
              <div className="text-[10px] tracking-[0.15em] uppercase mt-1.5" data-testid={`studio-last-login-${g.studio}`}>
                {g.last_login_at ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-cream2 text-ink rounded-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
                    Ultimo accesso: {new Date(g.last_login_at).toLocaleString("it-IT")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-cream2 text-slate1 rounded-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate1/60 inline-block" />
                    Mai entrato
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{view === "archived" ? "Progetti archiviati" : "Totale"}</div>
              {view === "active" ? (
                <>
                  <div className="font-display text-2xl text-ink">€ {(g.total_value || 0).toLocaleString("it-IT")}</div>
                  {!isTrader && <div className="text-xs text-success">+ € {(g.total_credit || 0).toFixed(2)} credito</div>}
                  {!isTrader && (g.is_redeemable ? (
                    <div className="mt-2 inline-block px-2.5 py-1 bg-gold/15 border border-gold/40 text-gold text-[10px] tracking-[0.15em] uppercase rounded-sm" data-testid={`adm-redeemable-${g.studio}`}>
                      Riscattabile €{(g.redeemable_amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </div>
                  ) : g.wallet_balance > 0 ? (
                    <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-slate1">
                      Wallet €{(g.wallet_balance || 0).toFixed(2)} · cap €{(g.redeemable_cap || 0).toFixed(0)}
                    </div>
                  ) : null)}
                </>
              ) : (
                <div className="font-display text-2xl text-rose-700">{g.projects.length}</div>
              )}
            </div>
          </div>
          <div className="divide-y divide-ink/10">
            {g.projects.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`adm-proj-${p.id}`}>
                <Folder size={18} className="text-slate1" />
                <div className="flex-1 min-w-0">
                  <div className="text-ink truncate">{p.name}</div>
                  <div className="text-xs text-slate1">
                    {p.client_name || "—"} · {p.files?.length || 0} file · {new Date(p.updated_at).toLocaleString("it-IT")}
                    {isTrader && p.delivery_date && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-cream2 border border-ink/15 rounded-sm text-ink">
                        <Calendar size={10} /> Consegna {new Date(p.delivery_date).toLocaleDateString("it-IT")}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase rounded-sm ${STATUS_COLORS[p.status]}`}>{p.status.replace("_", " ")}</span>
                {view === "archived" && p.abandon_reason && (
                  <span className="text-xs text-rose-700 italic max-w-[180px] truncate" title={p.abandon_reason}>&ldquo;{p.abandon_reason}&rdquo;</span>
                )}
                <div className="text-right w-28">
                  <div className="text-sm text-ink">{p.value > 0 ? `€ ${p.value.toLocaleString("it-IT")}` : "—"}</div>
                  {!isTrader && <div className="text-xs text-success">+€{(p.credit_earned || 0).toFixed(2)}</div>}
                </div>
                <button onClick={() => openProject(p)} className="text-[11px] tracking-[0.2em] uppercase text-ink underline" data-testid={`adm-open-${p.id}`}>Gestisci</button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {selected && (
        <div className="fixed inset-0 z-50 bg-ink/40" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-cream overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="adm-detail">
            <div className="p-6 border-b border-ink/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{selected.client_name || "Progetto"}</div>
                <div className="font-display text-xl text-ink">{selected.name}</div>
                <div className="text-xs text-slate1 mt-1">{selected.user_name} · {selected.studio}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate1 text-sm">Chiudi</button>
            </div>

            <div className="p-6 space-y-6">
              {selected.status === "abbandonato" && (
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-sm">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-rose-700 mb-1">Motivo abbandono</div>
                  <div className="text-sm text-ink">{selected.abandon_reason || "Non specificato"}</div>
                  <button onClick={() => restoreProject(selected.id)} className="mt-3 inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="adm-restore">
                    Ripristina progetto
                  </button>
                  <div className="text-xs text-slate1 mt-2">Tornerà allo stato precedente: <strong>{(selected.prev_status || "bozza").replace("_"," ")}</strong></div>
                </div>
              )}
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">Stato</label>
                <select value={selected.status} onChange={(e) => setStatus(selected.id, e.target.value)} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="adm-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">Valore Progetto (€)</label>
                <div className="flex gap-2">
                  <input type="number" min={0} step="100" value={valueInput} onChange={(e) => setValueInput(e.target.value)} className="flex-1 border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="adm-value-input" />
                  <button onClick={() => setValue(selected.id)} className="bg-ink text-cream px-5 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="adm-value-save">Valorizza</button>
                </div>
                <div className="text-xs text-slate1 mt-2">
                  {isTrader
                    ? "Il valore aggiornerà il volume totale del trader. Nessun cashback associato."
                    : `Verrà accreditato il 2% al wallet dello studio. Credito attuale: € ${(selected.credit_earned || 0).toFixed(2)}`}
                </div>
              </div>

              {isTrader && (
                <div className="p-4 bg-white border border-ink/10 rounded-sm" data-testid="adm-delivery-block">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-2 inline-flex items-center gap-1.5">
                    <Calendar size={11} /> Data consegna
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      defaultValue={(selected.delivery_date || "").slice(0, 10)}
                      onBlur={(e) => {
                        const next = e.target.value || "";
                        const curr = (selected.delivery_date || "").slice(0, 10);
                        if (next !== curr) saveDeliveryDate(selected.id, next);
                      }}
                      className="flex-1 border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm"
                      data-testid="adm-delivery-input"
                    />
                    {selected.delivery_date && (
                      <button
                        onClick={() => saveDeliveryDate(selected.id, "")}
                        className="text-[10px] tracking-[0.2em] uppercase text-slate1 hover:text-destructive px-3 py-2"
                        data-testid="adm-delivery-clear"
                      >
                        Rimuovi
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">Carica file di risposta</label>
                <input type="file" onChange={(e) => uploadFile(selected.id, e.target.files?.[0], "generic")} className="w-full border border-ink/20 p-3 rounded-sm bg-white" data-testid="adm-upload" />
              </div>

              {isTrader && (
                <div className="space-y-4">
                  {TRADER_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    const items = files.filter((f) => f.category === cat.key);
                    return (
                      <div key={cat.key} className="bg-white border border-gold/30 rounded-sm" data-testid={`adm-cat-${cat.key}`}>
                        <div className="px-4 py-3 border-b border-ink/10 flex items-center justify-between gap-3 bg-gold/5">
                          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-gold">
                            <Icon size={11} /> {cat.label}
                            <span className="ml-1 inline-flex items-center gap-1 text-slate1 normal-case tracking-normal text-[10px]">
                              <Lock size={9} /> Solo admin può caricare
                            </span>
                          </div>
                          <label className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-ink border border-ink/30 px-3 py-1.5 rounded-sm cursor-pointer hover:bg-ink hover:text-cream transition-colors">
                            + Carica
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => uploadFile(selected.id, e.target.files?.[0], cat.key)}
                              data-testid={`adm-upload-${cat.key}`}
                            />
                          </label>
                        </div>
                        {items.length === 0 ? (
                          <div className="p-4 text-sm text-slate1 italic">Nessun file in questa categoria.</div>
                        ) : (
                          <div className="divide-y divide-ink/10">
                            {items.map((f) => (
                              <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`adm-file-${cat.key}-${f.id}`}>
                                <FileText size={14} className="text-gold" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-ink truncate">{f.filename}</div>
                                  <div className="text-xs text-slate1">{new Date(f.created_at).toLocaleDateString("it-IT")} · {(f.size / 1024).toFixed(0)} KB</div>
                                </div>
                                <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink"><Download size={14} /></button>
                                {isFilePreviewable(f) && (
                                  <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" aria-label="Anteprima"><Eye size={14} /></button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-2">File Ricevuti dallo {isTrader ? "trader" : "studio"} ({files.filter(f => f.uploader_role !== "admin" && (!f.category || f.category === "generic")).length})</div>
                <div className="divide-y divide-ink/10 bg-white border border-ink/10 rounded-sm mb-4">
                  {files.filter(f => f.uploader_role !== "admin" && (!f.category || f.category === "generic")).length === 0 && <div className="p-4 text-sm text-slate1">Nessun file ricevuto.</div>}
                  {files.filter(f => f.uploader_role !== "admin" && (!f.category || f.category === "generic")).map((f) => (
                    <div key={f.id} className="p-3 flex items-center gap-3">
                      <FileText size={14} className="text-slate1" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink truncate">{f.filename}</div>
                        <div className="text-xs text-slate1">{f.uploader_name || (isTrader ? "Trader" : "Studio")} · {(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString("it-IT")}</div>
                      </div>
                      <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink"><Download size={14} /></button>
                      {isFilePreviewable(f) && (
                        <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" data-testid={`adm-prev-${f.id}`} aria-label="Anteprima"><Eye size={14} /></button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-2">File Inviati da ARCHI-HUB ({files.filter(f => f.uploader_role === "admin" && (!f.category || f.category === "generic")).length})</div>
                <div className="divide-y divide-ink/10 bg-white border border-ink/10 rounded-sm">
                  {files.filter(f => f.uploader_role === "admin" && (!f.category || f.category === "generic")).length === 0 && <div className="p-4 text-sm text-slate1">Nessun file inviato.</div>}
                  {files.filter(f => f.uploader_role === "admin" && (!f.category || f.category === "generic")).map((f) => (
                    <div key={f.id} className="p-3 flex items-center gap-3">
                      <FileText size={14} className="text-gold" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink truncate">{f.filename}</div>
                        <div className="text-xs text-slate1">{f.uploader_name || "ARCHI-HUB"} · {(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString("it-IT")}</div>
                      </div>
                      <button onClick={() => downloadFile(f.id, f.filename)} className="text-slate1 hover:text-ink"><Download size={14} /></button>
                      {isFilePreviewable(f) && (
                        <button onClick={() => previewFile(f)} disabled={filePreviewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" data-testid={`adm-prev-sent-${f.id}`} aria-label="Anteprima"><Eye size={14} /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filePreview && (
        <div className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex flex-col" onClick={closeFilePreview} data-testid="adm-file-preview-modal">
          <div className="flex items-center justify-between px-6 py-3 bg-ink text-cream" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm tracking-[0.15em] uppercase truncate">{filePreview.name}</div>
            <button onClick={closeFilePreview} className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" data-testid="adm-file-preview-close" aria-label="Chiudi anteprima">
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 bg-cream2" onClick={(e) => e.stopPropagation()}>
            {filePreview.isPdf ? (
              <iframe src={filePreview.url} title={filePreview.name} className="w-full h-full border-0" data-testid="adm-file-preview-iframe" />
            ) : filePreview.isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={filePreview.url} alt={filePreview.name} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate1 text-sm">Anteprima non supportata.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
